import { ActionIcon, Badge, Button, Divider, Tooltip } from '@mantine/core';
import { Calendar, CircleAlert, Clock, Columns2, User, X } from 'lucide-react';
import { FC } from 'react';

import Paragraph from '@/components/text/Paragraph.tsx';

import { PlacedAppointment } from '@/models/appointment/Appointment.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    patientName: string;
    appointmentTypeName: string;
    calendarName: string;
    durationMinutes: number;
    totalAppointments: number;
    placedAppointments: PlacedAppointment[];
    onClose: () => void;
    onBack: () => void;
    onConfirm: () => void;
    onRemove: (position: number) => void;
    isLoading?: boolean;
    confirmLabel?: string;
}

const SeriesPlacementPanel: FC<Props> = ({
    patientName,
    appointmentTypeName,
    calendarName,
    durationMinutes,
    totalAppointments,
    placedAppointments,
    onClose,
    onBack,
    onConfirm,
    onRemove,
    isLoading = false,
    confirmLabel = 'Erstellen',
}) => {
    const placedCount = placedAppointments.length;
    const nextPosition = placedCount + 1;
    const allPlaced = placedCount >= totalAppointments;

    return (
        <div className="flex w-full flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-md">
            {/* Header */}
            <div className="flex items-center justify-between">
                <Paragraph className="text-sm font-semibold">Manuelle Serienbuchung</Paragraph>
                <ActionIcon variant="subtle" color="slate" size="sm" onClick={onClose}>
                    <X className="size-4" />
                </ActionIcon>
            </div>

            <Divider />

            {/* Patient Info */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm">
                    <User className="size-4 shrink-0 text-slate-400" />
                    <Paragraph className="truncate">{patientName}</Paragraph>
                </div>
                <Tooltip label={appointmentTypeName}>
                    <div className="flex min-w-0 items-center gap-2 text-sm">
                        <div className="flex size-4 shrink-0 items-center justify-center">
                            <span className="size-2.5 rounded-full bg-violet-500" />
                        </div>
                        <Paragraph className="truncate">{appointmentTypeName}</Paragraph>
                        <Paragraph className="shrink-0">· {durationMinutes} Min.</Paragraph>
                    </div>
                </Tooltip>
                <Tooltip label={calendarName}>
                    <div className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
                        <Columns2 className="size-4 shrink-0" />
                        <Paragraph className="truncate">{calendarName}</Paragraph>
                    </div>
                </Tooltip>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="size-4 shrink-0" />
                    <Paragraph>{totalAppointments} Termine insgesamt</Paragraph>
                </div>
            </div>

            <Divider />

            {/* Progress */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <Paragraph className="text-sm font-semibold">Fortschritt</Paragraph>
                    <Badge variant="light" color="teal" size="lg" className="rounded-sm">
                        {placedCount} / {totalAppointments}
                    </Badge>
                </div>
                <div key={allPlaced ? 'placed' : 'placing'} className="flex items-start gap-2 text-xs text-slate-500">
                    {allPlaced ? (
                        <>
                            <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
                            <Paragraph>Alle Termine wurden platziert</Paragraph>
                        </>
                    ) : (
                        <>
                            <Clock className="mt-0.5 size-3.5 shrink-0" />
                            <Paragraph>Klicken Sie auf einen freien Slot für Termin #{nextPosition}</Paragraph>
                        </>
                    )}
                </div>
            </div>

            {/* Placed Appointments List */}
            {placedCount > 0 && (
                <div className="flex flex-col gap-2">
                    <Paragraph className="text-xs text-slate-500">Gebuchte Termine:</Paragraph>
                    <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200">
                        {placedAppointments.map((appointment, index) => (
                            <div
                                key={appointment.id}
                                className={`flex items-center justify-between px-3 py-2 ${
                                    index < placedCount - 1 ? 'border-b border-slate-100' : ''
                                } ${appointment.isPast ? 'opacity-50' : ''}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`flex size-5 items-center justify-center rounded-full text-xs font-medium ${
                                            appointment.isPast ? 'bg-slate-300' : 'bg-primary'
                                        }`}
                                    >
                                        {appointment.position}
                                    </span>
                                    <span className="text-sm font-medium">
                                        {dayjs(appointment.startDatetime).format('DD.MM.YYYY')}
                                    </span>
                                    <span className="text-sm text-slate-500">
                                        {dayjs(appointment.startDatetime).format('HH:mm')}
                                    </span>
                                </div>
                                {!appointment.isPast && (
                                    <ActionIcon
                                        variant="subtle"
                                        color="slate"
                                        size="xs"
                                        onClick={() => onRemove(appointment.position)}
                                    >
                                        <X className="size-3" />
                                    </ActionIcon>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Divider />

            {/* Footer */}
            <div className="flex gap-2">
                <Button variant="outline" fullWidth onClick={onBack}>
                    Zurück
                </Button>
                <Button fullWidth onClick={onConfirm} disabled={!allPlaced || isLoading} loading={isLoading}>
                    {confirmLabel}
                </Button>
            </div>
        </div>
    );
};

export default SeriesPlacementPanel;
